"use client";

import React from "react";
import { Phone, Mail, ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
export default function Finalcta() {
  const nearbyAreas = [{
    name: "Marlow",
    to: "/AreasWeCoverPage"
  }, {
    name: "High Wycombe",
    to: "/AreasWeCoverPage"
  }, {
    name: "Maidenhead",
    to: "/AreasWeCoverPage"
  }, {
    name: "Slough",
    to: "/AreasWeCoverPage"
  }, {
    name: "Windsor",
    to: "/AreasWeCoverPage"
  }, {
    name: "Bracknell",
    to: "/AreasWeCoverPage"
  }];
  const accreditations = ["NASC Member", "CHAS Accredited"];
  return <section id="finalcta" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative rounded-2xl bg-secondary text-secondary-foreground border border-border/40 p-8 sm:p-12 lg:p-16 shadow-xl overflow-hidden">
          {/* Subtle grid pattern background accent */}
          <div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="flex flex-wrap items-center gap-2.5">
                {accreditations.map((item, i) => <Badge key={item} data-index={i} variant="outline" className="border-secondary-foreground/20 text-secondary-foreground bg-secondary/60 text-xs px-3 py-1 font-medium">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1.5 text-primary shrink-0" />
                    {item}
                  </Badge>)}
              </div>

              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground font-default">
                  Request a scaffolding quote
                </h2>
                <p className="text-base sm:text-lg text-secondary-foreground/80 max-w-xl font-default leading-relaxed">
                  Speak directly with our team for honest technical advice, prompt site surveys, and transparent pricing across Berkshire, Surrey, Hampshire, and Buckinghamshire.
                </p>
              </div>

              {/* Direct Contacts Strip */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <a href="tel:01344929900" className="flex items-center gap-3 p-3.5 rounded-lg bg-card/10 hover:bg-card/20 border border-secondary-foreground/15 transition-[background-color,border-color] text-secondary-foreground group">
                  <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary text-primary-foreground shrink-0 shadow-xs">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-secondary-foreground/75 font-medium">Direct phone</div>
                    <div className="text-base font-semibold text-secondary-foreground group-hover:text-primary transition-colors">
                      01344 929900
                    </div>
                  </div>
                </a>

                <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-center gap-3 p-3.5 rounded-lg bg-card/10 hover:bg-card/20 border border-secondary-foreground/15 transition-[background-color,border-color] text-secondary-foreground group">
                  <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary text-primary-foreground shrink-0 shadow-xs">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-secondary-foreground/75 font-medium">Email inquiries</div>
                    <div className="text-sm font-semibold truncate text-secondary-foreground group-hover:text-primary transition-colors">
                      quotes@bmfscaffolding.co.uk
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Right Action Column */}
            <div className="lg:col-span-5 flex flex-col justify-center items-start lg:items-end space-y-4">
              <div className="w-full sm:w-auto flex flex-col gap-3">
                <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 text-base font-semibold h-13 px-8 shadow-md">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                    Request a quote
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>

                <p className="text-xs text-secondary-foreground/60 text-center lg:text-right">
                  Fast turnaround on tenders and commercial access schedules
                </p>
              </div>
            </div>
          </div>

          <Separator className="my-8 bg-secondary-foreground/15" />

          {/* Bottom Nearby Areas Row */}
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-secondary-foreground/70">
            <div className="flex items-center gap-2 font-medium text-secondary-foreground shrink-0">
              <MapPin className="w-4 h-4 text-primary" />
              <span>Nearby areas covered:</span>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {nearbyAreas.map((area, index) => <div key={area.name} data-index={index} className="flex items-center gap-4">
                  <Link to={area.to} className="text-secondary-foreground/80 hover:text-primary hover:underline transition-colors font-medium">
                    {area.name}
                  </Link>
                  {index < nearbyAreas.length - 1 && <span className="text-secondary-foreground/30 hidden sm:inline">•</span>}
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}